/*
 * What the Mapping page tells the user when the map can't do its job, instead of failing quietly
 * with a blank tab, an empty basemap, or the last tract's data still in the panel.
 */

/* Map-wide problems. The blocking ones gray the page out under a card; the others are a notice
 * over a map that still works. */
export type MapIssueKind = 'unreachable' | 'server-error' | 'not-configured' | 'map-failed' | 'tracts-failed' | 'config-failed';

/* What the page was doing when something failed, for the details view. */
export type MapLoadStep =
    | 'map-record'
    | 'config-file'
    | 'tracts-file'
    | 'map-view'
    | 'tracts-read'
    | 'tract-layer'
    | 'tract-detail'
    | 'mineral-owners'
    | 'leaseholds';

export interface IMapIssue {
    kind: MapIssueKind;
    step?: MapLoadStep;
    /* HTTP status of the failed request; absent when the server never answered. */
    status?: number;
    /* What the page found, in our own words (never text from the server). */
    finding?: string;
    /* When it happened, to line up with the server's logs. */
    at?: Date;
}

export interface IMapIssueView {
    title: string;
    /* Empty when the title says it all. */
    message: string;
    /* Blocking issues cover the page; the rest are a dismissible notice. */
    blocking: boolean;
    /* Whether trying again (a page reload) could help. */
    canRetry: boolean;
}

/*
 * How a use-http request ended, read from the hook's live `request.error` right after it settles.
 * On a network error use-http resolves with the hook's PREVIOUS data and leaves `response` as the
 * previous request's (response.ok can still read true), so the error is the only reliable signal.
 * use-http names the error after the HTTP status for an error response; a network error has none.
 */
export interface IRequestOutcome {
    failed: boolean;
    status?: number;
}

export const requestOutcome = (error: unknown): IRequestOutcome => {
    if (!error) {
        return { failed: false };
    }
    const status = Number((error as { name?: unknown }).name);
    return Number.isInteger(status) && status > 0 ? { failed: true, status } : { failed: true };
};

/* The map-wide issue when the map component itself fails: the view, or tracts it couldn't read or draw. */
export const mapLoadFailureIssue = (step: MapLoadStep): IMapIssue => ({
    kind: step === 'map-view' ? 'map-failed' : 'tracts-failed',
    step,
    finding:
        step === 'map-view'
            ? `the map view didn't start`
            : step === 'tracts-read'
            ? `the tracts file couldn't be read`
            : `the tract layer couldn't be drawn`,
    at: new Date(),
});

/* The map-wide issue for a request that failed while the page loaded its map. */
export const loadFailureIssue = (outcome: IRequestOutcome, step: MapLoadStep): IMapIssue => ({
    kind: outcome.status ? (step === 'tracts-file' ? 'tracts-failed' : 'server-error') : 'unreachable',
    step,
    status: outcome.status,
    at: new Date(),
});

const TRACT_RECORD_NAMES: Partial<Record<MapLoadStep, string>> = {
    'tract-detail': 'details',
    'mineral-owners': 'mineral owners',
    leaseholds: 'leaseholds',
};

/*
 * The side panel's note when some of a clicked tract's records didn't load. The panel stays open on
 * what did load and what the map file has. A tract LandHawk doesn't have (the tract route answers
 * "Tract not found." with a 400, or a 404) says so plainly: the map file can carry a tract the
 * database doesn't. Undefined when everything loaded.
 */
export const tractLoadNote = (failures: Array<{ step: MapLoadStep; status?: number }>): string | undefined => {
    if (!failures.length) {
        return undefined;
    }
    const detail = failures.find((f) => f.step === 'tract-detail');
    if (detail && (detail.status === 400 || detail.status === 404)) {
        return `This tract isn't in LandHawk, so the panel shows what the map file has for it.`;
    }
    const names = failures.map((f) => TRACT_RECORD_NAMES[f.step]).filter((n, i, all): n is string => !!n && all.indexOf(n) === i);
    const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] || 'records';
    return `This tract's ${list} couldn't load. Select the tract again to retry.`;
};

export const mapIssueView = (issue: IMapIssue): IMapIssueView => {
    if (issue.kind === 'unreachable') {
        return {
            title: `Can't reach the Kudu server`,
            message: 'The map needs the server to load this project. Check your connection, then try again.',
            blocking: true,
            canRetry: true,
        };
    }
    if (issue.kind === 'server-error') {
        return issue.status === 401
            ? { title: 'Your sign-in has expired', message: 'Reload the page to sign in again.', blocking: true, canRetry: true }
            : {
                  title: `The map couldn't load`,
                  message: 'The server ran into a problem loading this project. Try again in a moment; if it keeps happening, let the GIS team know.',
                  blocking: true,
                  canRetry: true,
              };
    }
    if (issue.kind === 'not-configured') {
        return {
            title: `This project's map isn't set up yet`,
            message: `Its tracts haven't been uploaded. The GIS team sets up a project's map in the GIS Portal.`,
            blocking: true,
            canRetry: false,
        };
    }
    if (issue.kind === 'map-failed') {
        return {
            title: `The map couldn't start`,
            message: `The map itself couldn't load in this browser. Try again; if it keeps happening, let the GIS team know.`,
            blocking: true,
            canRetry: true,
        };
    }
    if (issue.kind === 'tracts-failed') {
        // Worded so it's true whether the tracts failed or the project simply has no mapping. The title
        // says it all: nothing to try and nothing to pass on.
        return {
            title: 'Tract layer unavailable',
            message: '',
            blocking: true,
            canRetry: false,
        };
    }
    return {
        title: `Map configuration didn't load`,
        message: `This project's map configuration couldn't be loaded, so the map shows the default colors and layers.`,
        blocking: false,
        canRetry: false,
    };
};

const STEP_LABELS: Record<MapLoadStep, string> = {
    'map-record': `Loading the project's map settings`,
    'config-file': 'Downloading the map configuration',
    'tracts-file': 'Downloading the tracts',
    'map-view': 'Starting the map',
    'tracts-read': 'Reading the tracts',
    'tract-layer': 'Drawing the tracts',
    'tract-detail': `Loading the tract's details`,
    'mineral-owners': `Loading the tract's mineral owners`,
    leaseholds: `Loading the tract's leaseholds`,
};

/* Our own words for the codes a user is likely to see; the server's text is never shown. */
const STATUS_WORDS: Record<number, string> = {
    400: 'request refused',
    401: 'not signed in',
    403: 'no access',
    404: 'not found',
    408: 'timed out',
    429: 'too many requests',
    500: 'server error',
    502: 'bad gateway',
    503: 'service unavailable',
    504: 'gateway timeout',
};

/*
 * The hidden-until-asked details for an error: what the page was doing, the server's status code,
 * what it found, which project or tract, and when. Deliberately nothing else: no text from the
 * server's reply, no addresses, ids or keys. (A file download by S3 id needs no sign-in, so those
 * ids in particular must never show.) Safe to show anyone and to paste into a support request.
 */
export const errorDetails = (issue: { step?: MapLoadStep; status?: number; finding?: string; at?: Date }, subject?: string): string[] =>
    [
        issue.step ? `While: ${STEP_LABELS[issue.step]}` : '',
        issue.status
            ? `Server response: ${issue.status}${STATUS_WORDS[issue.status] ? ` (${STATUS_WORDS[issue.status]})` : ''}`
            : issue.step && !issue.finding
            ? 'Server response: none (no connection)'
            : '',
        issue.finding ? `Found: ${issue.finding}` : '',
        subject || '',
        issue.at ? `Time: ${issue.at.toLocaleString()}` : '',
    ].filter((line) => line !== '');



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/mapHealth.ts